/**
 * One CSV export of a client-stitched table (ADR 0020, OD-EXPORT-001).
 *
 * A stitched widget's table holds only the keys columns, and stitched values exist only for rows
 * in view, so the export can't read either. It creates a one-off full table from today's
 * definition with every template column, waits until the table finishes construction, snapshots
 * it, and releases it. A table stuck constructing is snapshotted when the timeout passes, so the
 * export still gives the rows the table holds. Disposing the export aborts it and releases the
 * table; a root holds at most one in a `DisposableBox`, so a new export supersedes the last.
 */
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { TFunction } from 'i18next';
import { exportTableToCSV } from 'src/lib/util/exportTableCSV';
import type { ExportTableCSVResult } from 'src/lib/util/exportTableCSV';
import type { TableCreationQuery, TableModel } from 'src/models/TableModel';
import { waitForTableConstruction } from 'src/models/TableModel/waitForTableConstruction';

/** How long an export waits for its table to finish construction before snapshotting it anyway. */
export const FULL_TABLE_EXPORT_TIMEOUT_MS = 15_000;

/** The live tables' bounds, so construction progress means what the status icon shows. */
const EXPORT_TABLE_BOUNDS = { firstRow: 0, windowSize: 100 };

export interface FullTableExportOptions {
    readonly createTable: () => TableModel;
    /** The full table's query, with every template column and a `userViewKey` of its own. */
    readonly queryOptions: TableCreationQuery;
    /** Display names by column id; only these columns are exported. */
    readonly columnNames: Record<string, string>;
    readonly t: TFunction;
    readonly logger: Logger;
    readonly devtoolsName: string;
    readonly timeoutMs?: number;
}

export class FullTableExport implements Disposable {
    readonly #abortController = new AbortController();
    readonly #lifetime = new DisposableStack();
    /** Settles once the download starts or the export fails; `aborted` once disposed. */
    readonly result: Promise<ExportTableCSVResult>;

    constructor(options: FullTableExportOptions) {
        const table = this.#lifetime.use(options.createTable());
        table.init({
            queryOptions: options.queryOptions,
            initBounds: EXPORT_TABLE_BOUNDS,
            devtoolsName: `${options.devtoolsName}/export`,
        });
        this.result = this.#run(table, options);
    }

    async #run(table: TableModel, options: FullTableExportOptions): Promise<ExportTableCSVResult> {
        const { signal } = this.#abortController;
        const timeoutMs = options.timeoutMs ?? FULL_TABLE_EXPORT_TIMEOUT_MS;
        try {
            const wait = await waitForTableConstruction(table, { timeoutMs, signal });
            if (wait === 'timed-out') {
                options.logger.warn({
                    message: 'Exporting a full table that did not finish construction',
                    tableKey: table.key,
                    connectionStatus: table.connectionStatus.value,
                    progress: table.progress.value,
                    timeoutMs,
                });
            }
            return await exportTableToCSV(table, options.columnNames, options.t, { signal });
        } catch (error) {
            if (signal.aborted) {
                return { success: false, error: 'aborted' };
            }
            throw error;
        } finally {
            this.#lifetime.dispose();
        }
    }

    [Symbol.dispose](): void {
        this.#abortController.abort();
        this.#lifetime.dispose();
    }
}
